import axios from 'axios'
import i18n from '@/i18n'
import Vue from 'vue'

/**
 * Get the first item that pass the test
 * by second argument function
 *
 * @param {Array} list
 * @param {Function} f
 * @return {*}
 */
export function find (list, f) {
  return list.filter(f)[0]
}

/**
 * Deep copy the given object considering circular structure.
 * This function caches all nested objects and its copies.
 * If it detects circular structure, use cached copy to avoid infinite loop.
 *
 * @param {*} obj
 * @param {Array<Object>} cache
 * @return {*}
 */
export function deepCopy (obj, cache = []) {
  // just return if obj is immutable value
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // if obj is hit, it is in circular structure
  const hit = find(cache, c => c.original === obj)
  if (hit) {
    return hit.copy
  }

  const copy = Array.isArray(obj) ? [] : {}
  // put the copy into cache at first
  // because we want to refer it in recursive deepCopy
  cache.push({
    original: obj,
    copy
  })

  Object.keys(obj).forEach(key => {
    copy[key] = deepCopy(obj[key], cache)
  })

  return copy
}

/**
 * forEach for object
 */
export function forEachValue (obj, fn) {
  Object.keys(obj).forEach(key => fn(obj[key], key))
}

export function isObject (obj) {
  return obj !== null && typeof obj === 'object'
}

export function isPromise (val) {
  return val && typeof val.then === 'function'
}

export function assert (condition, msg) {
  if (!condition) throw new Error(`[vuex] ${msg}`)
}

export function partial (fn, arg) {
  return function () {
    return fn(arg)
  }
}

export function objectFilter (obj, predicate) {
  const result = {}
  let key

  for (key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && predicate(obj[key])) {
      result[key] = obj[key]
    }
  }

  return result
}

/**
 * find menu name for route path
 * @param menus
 * @param routePath ex) /manager/list
 * @returns {string}
 */
export function findMenuName (menu, routePath) {
  const paths = routePath.split('/')
  const menuObj = searchMenu(null, menu, paths)
  return menuObj != null ? menuObj.title : ''
}

function searchMenu (parent, child, paths) {
  if (parent !== null && parent.group === ('/' + paths[1]) && child.to && child.to === paths[2]) {
    return child
  } else if (child.children) {
    for (let i = 0; i < child.children.length; i++) {
      const result = searchMenu(child, child.children[i], paths)
      if (result != null) {
        return result
      }
    }
  }
  return null
}

export function dayToKr (day) {
  const days = ['일', '월', '화', '수', '목', '금', '토']
  return days[day]
}

export function setCookie (cName, cValue, days = 365) {
  // document.cookie = cname + '=' + cvalue
  const d = new Date()
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000))
  const expires = 'expires=' + d.toUTCString()
  document.cookie = cName + '=' + cValue + ';' + expires + ';path=/'
}

export function deleteCookie (cname) {
  document.cookie = cname + '= ; max-age=-1'
}

export function getCookie (name) {
  const value = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)')
  return value ? value[2] : null
}

export const getLocalStorage = (key) => {
  return JSON.parse(decodeURIComponent(localStorage.getItem(key)))
}

export const setLocalStorage = (key, value) => {
  return localStorage.setItem(key, encodeURIComponent(JSON.stringify(value)))
}

export const getLangData = () => {
  return getLocalStorage('lang:data')
}

export const setLangData = (value) => {
  return setLocalStorage('lang:data', value)
}

export function getLang (locale, toName) {
  const name = toName ? toName.toLowerCase() : null

  const request = (locale, langName) => {
    axios.get(`/static/locales/${locale}/${langName}.json`, {
      params: {
        t: process.env.VUE_APP_VERSION // NOTE: disable browser cache?
      }
    }).then(({ data }) => {
      const localLang = getLangData()
      i18n.mergeLocaleMessage(locale, data.data || {})

      if (localLang) {
        const newValue = {
          ...localLang,
          [locale]: {
            ...localLang[locale],
            [langName]: {
              version: process.env.VUE_APP_VERSION,
              ...data
            }
          }
        }
        setLangData(newValue)
      } else {
        const newValue = {
          [locale]: {
            [langName]: {
              version: process.env.VUE_APP_VERSION,
              ...data
            }
          }
        }
        setLangData(newValue)
      }
    }).catch(e => {
      console.log(e)
    })
  }

  if (name) {
    try {
      const localCategory = getLangData()[locale][name]
      const comparison = localCategory.version !== window.LOCALE_VERSION[locale][name]
      if (comparison) {
        request(locale, name)
      }
    } catch (e) {
      request(locale, name)
    }
  }
}

const getLightnessOfRGB = (rgb) => {
  const rgbIntArray = [rgb.r, rgb.g, rgb.b]

  // Get the highest and lowest out of red green and blue
  const highest = Math.max(...rgbIntArray)
  const lowest = Math.min(...rgbIntArray)

  return (highest + lowest) / 2 / 255
}

export const getAverageRGB = (el, callback) => {
  const blockSize = 5 // only visit every 5 pixels
  const defaultRGB = { r: 0, g: 0, b: 0 } // for non-supporting envs

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const context = canvas.getContext && canvas.getContext('2d')

  const rgb = { r: 0, g: 0, b: 0 }
  let count = 0

  if (!context) {
    return defaultRGB
  }

  const img = new Image()
  // img.src = el.src + `?time=${new Date().getTime()}`
  img.src = el.src
  img.crossOrigin = 'Anonymous'

  img.onload = function () {
    ctx.drawImage(this, 0, 0)

    let data = null

    try {
      data = context.getImageData(0, 0, 100, 100)
    } catch (e) {
      /* security error, img on diff domain */
      return defaultRGB
    }

    const length = data.data.length
    let i = -4
    while ((i += blockSize * 4) < length) {
      ++count
      rgb.r += data.data[i]
      rgb.g += data.data[i + 1]
      rgb.b += data.data[i + 2]
    }

    // ~~ used to floor values
    rgb.r = ~~(rgb.r / count)
    rgb.g = ~~(rgb.g / count)
    rgb.b = ~~(rgb.b / count)

    const lightness = getLightnessOfRGB(rgb)
    let style = 'default'

    if (lightness < 0.8) {
      style = 'transparent'
    }

    callback(style)
  }
}

export function setLanguage (lang) {
  // 처음 접속시 cookie에 lang값이 없으면 추가
  // if (!Vue.prototype.$cookie.get('lang')) {
  Vue.prototype.$cookie.set('lang', lang === 'ko' ? 'KR' : lang.toUpperCase())
  // }

  document.documentElement.lang = lang

  const favicon = document.querySelector('head link[rel="icon"]')
  // eslint-disable-next-line
  favicon.setAttribute('href', favicon.href.replace(/\/[en|ko]+\//i, `/${lang}/`))

  document.title = lang === 'en' ? process.env.VUE_APP_TITLE_EN : process.env.VUE_APP_TITLE
}

// 첫글자 대문자
export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
